/* eslint-disable react/jsx-curly-newline */
/* eslint-disable indent */
/* eslint-disable no-confusing-arrow */
import React from 'react';
import Swal from 'sweetalert2';
import { Box, Button, Grid } from '@material-ui/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import CreateContentTranslation from 'components/CreateContentTranslation/CreateContentTranslation';
import useStyles from './styles';
import { StatusType } from '../../../../enums/status-type.enum';
import { IValidationResult } from '../../../../services/ContentValidator/interfaces/validation.interface';

interface IActionButtonsProps {
  permissions: IPermission;
  user: IUser;
  onSaveContent: (
    status: number,
    sendWp?: boolean,
    closeDrawer?: boolean,
    autosave?: boolean,
  ) => Promise<void>;
  onUpdateContent: (
    status: number,
    sendWp?: boolean,
    closeDrawer?: boolean,
    autosave?: boolean,
  ) => Promise<void>;
  buttonsDisabled: boolean;
  checkEmptyFields: (saveCheck: boolean) => boolean;
  status: number;
  setValidationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setValidationResult: React.Dispatch<React.SetStateAction<IValidationResult>>;
  validationResult: IValidationResult;
  validate: () => IValidationResult;
  titleId: string;
  inReview: boolean;
  onRejectContent: () => void;
  isStartRevision?: boolean;
  TimerToReview?: JSX.Element;
  siteDomain: string;
}

const ActionButtons: React.FC<IActionButtonsProps> = ({
  permissions,
  user,
  onSaveContent,
  onUpdateContent,
  buttonsDisabled,
  checkEmptyFields,
  status,
  setValidationModalOpen,
  validationResult,
  setValidationResult,
  validate,
  titleId,
  inReview,
  onRejectContent,
  isStartRevision,
  TimerToReview,
  siteDomain,
}) => {
  const classes = useStyles();

  const rejectContentPermittedRoles = ['CONTENTMANAGER', 'REVIEWER'];

  const confirmAlert = async (subtitle: string) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      html: subtitle,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#78001b',
      reverseButtons: true,
      confirmButtonText: 'Confirm',
      customClass: {
        container: classes.swalDialog,
      },
    });
    return result.value;
  };

  /**
   * Check if there is currently any analysis error
   */
  const hasAnalysisErrors = (result?: IValidationResult) => {
    return result ? !!result.errors.length : !!validationResult.errors.length;
  };

  const disableSendToRevisionButton = (buttonsDisabled: boolean) =>
    hasAnalysisErrors() || buttonsDisabled;

  const confirmAction = async (msg: string, action: number) => {
    if (checkEmptyFields(false)) {
      return;
    }
    if (await confirmAlert(msg)) {
      onUpdateContent(action, true, true);
    }
  };

  const openAnalysisModal = () => {
    setValidationModalOpen(true);
  };

  /**
   * Run validator and return it's result. If return `true` then there is a error
   */
  const checkIfHasAnalysisErrors = (): boolean => {
    if (!permissions.validations) return false;
    if (status !== StatusType.Working) return false;

    const newValidationResult = validate();
    setValidationResult(newValidationResult);

    return hasAnalysisErrors(newValidationResult);
  };

  return (
    <Grid xs={12} item>
      <Box display="flex" flexDirection="column">
        <Box display="flex" alignItems="center" marginBottom="12px">
          <Button
            onClick={openAnalysisModal}
            disabled={buttonsDisabled}
            fullWidth
            variant="contained"
            style={{
              backgroundColor: hasAnalysisErrors() ? '#EE6969' : '#50C964',
            }}
            className={classes.buttonAnalysis}
          >
            {hasAnalysisErrors() ? (
              <FontAwesomeIcon color="white" icon={['far', 'frown']} />
            ) : (
              <FontAwesomeIcon color="white" icon={['far', 'smile']} />
            )}
          </Button>
        </Box>

        {isStartRevision && TimerToReview}

        {inReview && rejectContentPermittedRoles.includes(user.role) && (
          <Box marginBottom="8px">
            <Button
              onClick={() => onRejectContent()}
              fullWidth
              variant="contained"
              color="primary"
              className={classes.buttonReject}
            >
              Return to writer
            </Button>
          </Box>
        )}

        {[
          StatusType.Revision,
          StatusType.Published,
          StatusType.ErrorWhenTranslating,
        ].includes(status) &&
          ['ADMIN', 'CONTENTMANAGER'].includes(user.role) && (
            <Box marginBottom="8px">
              <CreateContentTranslation
                titleId={titleId}
                siteDomain={siteDomain}
              />
            </Box>
          )}

        {permissions.showSendToRevisionButton && (
          <Box marginBottom="8px">
            <Button
              onClick={() =>
                checkIfHasAnalysisErrors()
                  ? openAnalysisModal()
                  : confirmAction(
                      'Are you sure that you want to send this content to revision?',
                      StatusType.Revision,
                    )
              }
              fullWidth
              disabled={
                ['WRITER', 'EDITOR'].includes(user.role)
                  ? disableSendToRevisionButton(buttonsDisabled)
                  : buttonsDisabled
              }
              variant="contained"
              color="primary"
            >
              Send to revision
            </Button>
          </Box>
        )}

        <Box marginBottom="8px">
          <Button
            onClick={() => onSaveContent(status, false, false)}
            disabled={buttonsDisabled}
            fullWidth
            variant="contained"
            className={classes.buttonSave}
          >
            Save
          </Button>
        </Box>
      </Box>
    </Grid>
  );
};

export default ActionButtons;
